import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PaymentTemplateCard from './components/PaymentTemplateCard';
import PaymentTemplateModal from './components/PaymentTemplateModal';
import MilestoneBuilder from './components/MilestoneBuilder';
import NotificationSettings from './components/NotificationSettings';
import { getTranslation } from '../../utils/translations';

const AdvancedPaymentConfiguration = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [currentLanguage, setCurrentLanguage] = useState('FR');
  const [activeTab, setActiveTab] = useState('templates');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [paymentTemplates, setPaymentTemplates] = useState([
    {
      id: '1',
      name: '30% à la commande, 70% avant livraison',
      description: 'Template standard pour les traitements orthodontiques',
      phases: [
        { phaseNumber: 1, phaseName: 'Acompte initial', percentage: 30, daysOffset: 0 },
        { phaseNumber: 2, phaseName: 'Solde avant livraison', percentage: 70, daysOffset: 30 }
      ],
      isActive: true,
      usageCount: 45
    },
    {
      id: '2',
      name: '50% à la commande, 50% à la livraison',
      description: 'Paiement équilibré en deux phases',
      phases: [
        { phaseNumber: 1, phaseName: 'Premier versement', percentage: 50, daysOffset: 0 },
        { phaseNumber: 2, phaseName: 'Paiement final', percentage: 50, daysOffset: 45 }
      ],
      isActive: true,
      usageCount: 28
    },
    {
      id: '3',
      name: 'Paiement en 3 fois',
      description: 'Facilité de paiement pour traitements longs',
      phases: [
        { phaseNumber: 1, phaseName: 'Acompte', percentage: 33.33, daysOffset: 0 },
        { phaseNumber: 2, phaseName: 'Deuxième versement', percentage: 33.33, daysOffset: 30 },
        { phaseNumber: 3, phaseName: 'Solde final', percentage: 33.34, daysOffset: 60 }
      ],
      isActive: true,
      usageCount: 12
    }
  ]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('appLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const t = getTranslation(currentLanguage);

  const breadcrumbItems = [
    { label: 'Accueil', path: '/dashboard' },
    { label: 'Facturation', path: '/billing-overview' },
    { label: 'Configuration Paiements' }
  ];

  const tabs = [
    { id: 'templates', label: 'Définir les échéanciers', icon: 'FileText' },
    { id: 'builder', label: 'Appliquer les échéanciers', icon: 'Layers' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' }
  ];

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = (templateId) => {
    if (window.confirm('Supprimer ce template ?')) {
      setPaymentTemplates(paymentTemplates?.filter(t => t?.id !== templateId));
    }
  };

  const handleSaveTemplate = (templateData) => {
    if (selectedTemplate) {
      setPaymentTemplates(paymentTemplates?.map(t => 
        t?.id === selectedTemplate?.id ? { ...templateData, id: t?.id, usageCount: t?.usageCount } : t
      ));
    } else {
      setPaymentTemplates([...paymentTemplates, { ...templateData, id: Date.now()?.toString(), usageCount: 0 }]);
    }
    setShowTemplateModal(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

        
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Configuration Avancée des Paiements
                </h1>
                <Breadcrumb items={breadcrumbItems} />
              </div>
              {activeTab === 'templates' && (
                <Button onClick={handleCreateTemplate}>
                  <Icon name="Plus" size={18} className="mr-2" />
                  Nouveau Template
                </Button>
              )}
            </div>

            {/* Tabs Navigation */}
            <div className="bg-card border border-border rounded-lg p-2 flex gap-2">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'templates' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paymentTemplates?.map(template => (
                  <PaymentTemplateCard
                    key={template?.id}
                    template={template}
                    language={currentLanguage}
                    onEdit={handleEditTemplate}
                    onDelete={handleDeleteTemplate}
                  />
                ))}
              </div>
            )}

            {activeTab === 'builder' && (
              <MilestoneBuilder language={currentLanguage} templates={paymentTemplates} />
            )}

            {activeTab === 'notifications' && (
              <NotificationSettings language={currentLanguage} />
            )}
          </div>
        </div>
      </div>
      {showTemplateModal && (
        <PaymentTemplateModal
          template={selectedTemplate}
          language={currentLanguage}
          onClose={() => setShowTemplateModal(false)}
          onSave={handleSaveTemplate}
        />
      )}
    </div>
  );
};

export default AdvancedPaymentConfiguration;